import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { track, trackLead } from "../lib/analytics";
import { motion } from "motion/react";
import {
  Search, Zap, BarChart3, FileText, CheckCircle2, ArrowRight,
  Star, ShieldCheck, Layers, Globe, Sparkles,
} from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { TopBanner } from "../components/landing/TopBanner";
import MegaNav from "../components/MegaNav";
import MegaFooter from "../components/MegaFooter";
// Above-the-fold — eager so hero paints immediately
import { AIEnginesMarquee } from "../components/landing/AIEnginesMarquee";
import HeroConsole from "../components/landing/graphics/HeroConsole";
import WorkflowStrip from "../components/landing/graphics/WorkflowStrip";
// Below-fold custom graphics — lazy so they don't weigh down the hero bundle
const ResultsPanel = lazy(() => import("../components/landing/graphics/ResultsPanel"));
const AICitationShowcase = lazy(() => import("../components/landing/graphics/AICitationShowcase"));
const ToolResultCard = lazy(() => import("../components/landing/graphics/ToolResultCard"));
import SEO, { organizationSchema, websiteSchema, softwareApplicationSchema, faqSchema } from "../components/SEO";
import { getAllPosts } from "../data/blogPosts";

// Below-the-fold — lazy (separate chunks, load when user scrolls or idle)
const LogoMarquee = lazy(() => import("../components/landing/LogoMarquee").then(m => ({ default: m.LogoMarquee })));
const ComparisonTable = lazy(() => import("../components/landing/ComparisonTable").then(m => ({ default: m.ComparisonTable })));
const NewFeaturesSection = lazy(() => import("../components/landing/NewFeaturesSection").then(m => ({ default: m.NewFeaturesSection })));
const VideoCard = lazy(() => import("../components/landing/VideoCard").then(m => ({ default: m.VideoCard })));
const ConversionSections = lazy(() => import("../components/landing/ConversionSections").then(m => ({ default: m.ConversionSections })));
const PricingSection = lazy(() => import("../components/landing/PricingSection").then(m => ({ default: m.PricingSection })));

// Simple placeholder shown while below-fold chunks stream in
const SectionFallback = () => <div style={{ minHeight: 120 }} aria-hidden="true" />;

/* ─── AUTH MODAL ─── */
function AuthModal({ open, onClose }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        const result = await login(email, password);
        onClose();
        // If user's email isn't verified yet, send them to verify page
        if (result?.success && result?.email_verified === false) {
          navigate("/verify-email");
        } else if (result?.success) {
          navigate(result.role === "admin" ? "/admin" : "/dashboard");
        }
      } else {
        // Register — creates user + sends verification code
        await API.post("/auth/register", { email, password, name: email.split("@")[0] });
        onClose();
        // After signup, always go to verify-email page (cookies are already set server-side)
        navigate("/verify-email");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl w-full max-w-sm p-6 sm:p-8 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-4 text-zinc-400 hover:text-zinc-900 text-xl font-bold">&times;</button>
        <div className="flex items-center gap-2 mb-6">
          <Logo />
        </div>
        <div className="flex gap-0 mb-5 border-b border-zinc-200">
          <button onClick={() => { setMode("login"); setError(""); }} className={`px-4 py-2 text-xs font-semibold tracking-wide uppercase border-b-2 transition-colors ${mode === "login" ? "border-[#FF2F2F] text-zinc-900" : "border-transparent text-zinc-400"}`}>Log In</button>
          <button onClick={() => { setMode("signup"); setError(""); }} className={`px-4 py-2 text-xs font-semibold tracking-wide uppercase border-b-2 transition-colors ${mode === "signup" ? "border-[#FF2F2F] text-zinc-900" : "border-transparent text-zinc-400"}`}>Sign Up</button>
        </div>
        {error && <p className="text-xs text-[#FF2F2F] border border-red-200 bg-red-50 p-2 rounded mb-4">{error}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-1 block">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF2F2F] transition-colors" placeholder="you@company.com" data-testid="auth-email-input" />
          </div>
          <div>
            <label className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-400 mb-1 block">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF2F2F] transition-colors" placeholder="Min 6 characters" data-testid="auth-password-input" />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-[#FF2F2F] text-white text-sm font-semibold py-3 rounded-lg hover:bg-[#e02828] transition-colors disabled:opacity-50" data-testid="auth-submit-btn">
            {loading ? "Please wait..." : mode === "login" ? "Log In" : "Create Account"}
          </button>
        </form>
        <p className="text-xs text-zinc-400 text-center mt-4">
          {mode === "login" ? "Don't have an account?" : "Already have an account?"}
          <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }} className="text-[#FF2F2F] font-semibold ml-1 hover:underline">
            {mode === "login" ? "Sign Up" : "Log In"}
          </button>
        </p>
      </motion.div>
    </div>
  );
}

/* ─── VIDEO ROWS ─── */
const row1Videos = ["AIiWxLlXUUw", "umEPygtui7k", "epwJTgByPqs", "dsSMqSOn-4o", "z1OK5XtXfr0", "kESedLpT2s0"];
const row2Videos = ["tQU-iCniLsU", "WgRUIU82XQw", "VSXNU6ScrsY", "MprlpKtDhWw", "wB8nLlZeGHw", "JJ4LwoxbkqI", "o-mulMWbXSc"];

const _BLOG_TODAY = new Date().toISOString().split("T")[0];

// Static fallback — used as initial render and as backfill when fewer than
// 3 dynamic posts have been published. Sorted newest-first.
const STATIC_HOME_POSTS = getAllPosts()
  .filter((p) => !p.publishDate || p.publishDate <= _BLOG_TODAY)
  .sort((a, b) => (b.publishDate || "").localeCompare(a.publishDate || ""));

const initialHomeBlogPosts = STATIC_HOME_POSTS.slice(0, 3);

// Pull a normalized ISO date for sort + display fallback.
function _isoDate(p) {
  return p.publishDate || p.date || "";
}

/**
 * Merge dynamic API posts with static posts, dedupe by slug, sort newest-first,
 * keep top 3. Pure function for testability.
 */
function pickTopHomeBlogPosts(apiPosts, staticPosts) {
  const apiSlugs = new Set((apiPosts || []).map((p) => p.slug));
  const merged = [
    ...(apiPosts || []),
    ...staticPosts.filter((p) => !apiSlugs.has(p.slug)),
  ];
  return merged
    .sort((a, b) => _isoDate(b).localeCompare(_isoDate(a)))
    .slice(0, 3);
}

const CAT_COLORS = {
  Content:         "bg-blue-50 text-blue-600",
  "AI SEO":        "bg-violet-50 text-violet-600",
  GEO:             "bg-emerald-50 text-emerald-600",
  "Rank Tracking": "bg-amber-50 text-amber-600",
  Keywords:        "bg-sky-50 text-sky-600",
};
const homeCatCls = (cat) => CAT_COLORS[cat] || "bg-[#FF2F2F]/10 text-[#FF2F2F]";

/* ═══════════════════════════════════════════════════════════════
   MAIN HOMEPAGE
   ═══════════════════════════════════════════════════════════════ */
export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [homeBlogPosts, setHomeBlogPosts] = useState(initialHomeBlogPosts);

  // Auto-pull the latest 3 published posts (dynamic + static) on mount.
  // Falls back silently to the static initial render if the API is unreachable.
  useEffect(() => {
    let cancelled = false;
    API.get("/blog/posts")
      .then((r) => {
        if (cancelled) return;
        const apiPosts = (r.data && r.data.posts) || [];
        setHomeBlogPosts(pickTopHomeBlogPosts(apiPosts, STATIC_HOME_POSTS));
      })
      .catch(() => {
        // Keep the static fallback already in state.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [checklistEmail, setChecklistEmail] = useState("");
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistDone, setChecklistDone] = useState(false);

  const handleChecklistSubmit = async (e) => {
    e.preventDefault();
    if (!checklistEmail || !checklistEmail.includes("@")) return;
    setChecklistLoading(true);
    // Submit to our backend so the lead also flows into Chatwoot with an
    // AI summary (not just ConvertKit). Backend handles the ConvertKit push
    // server-side via services/convertkit.
    try {
      await API.post("/demo/capture-email", {
        email: checklistEmail,
        source: "geo-audit-checklist",
      });
      trackLead("demo", { magnet: "geo-audit-checklist" });
    } catch (_) { /* still confirm — never block user on backend hiccup */ }
    setChecklistLoading(false);
    setChecklistDone(true);
  };
  const [isHoveredRow1, setIsHoveredRow1] = useState(false);
  const [isHoveredRow2, setIsHoveredRow2] = useState(false);

  useEffect(() => {
    if (location.pathname === "/login") setAuthOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, navigate]);

  const handleAnalyze = async () => {
    if (!keyword) return;
    setIsAnalyzing(true);
    try {
      await API.post("/demo/analyze", { query: keyword, type: "keyword" });
      track("demo_run", { tool: "keyword_analyzer", query_length: keyword.length });
    } catch (e) { /* ignore */ }
    setIsAnalyzing(false);
    setShowResults(true);
  };

  const handleSelectPlan = (planId, price) => {
    if (price === 0) {
      setAuthOpen(true);
    } else {
      navigate(`/checkout?plan=${planId}`);
    }
  };

  const handleEmailCapture = async () => {
    if (!email || !email.includes("@")) return;
    try {
      await API.post("/demo/capture-email", { email, source: "hero" });
      trackLead("demo", { magnet: "hero-email" });
    } catch (e) { /* ignore */ }
    setAuthOpen(true);
  };

  const homepageFaqs = [
    { question: "What is SEOintent?", answer: "SEOintent is an AI-powered SEO automation platform for agencies and small businesses. It auto-clusters keywords, generates SEO-optimized articles with Claude AI, optimizes to RankMath 90+, and publishes directly to WordPress or Shopify." },
    { question: "How is SEOintent different from Surfer SEO or Clearscope?", answer: "SEOintent is an all-in-one AI SEO platform — not just a content optimizer. It handles keyword research, clustering, content generation, internal linking, CMS publishing, rank tracking, Reddit agent, brand mentions, and technical SEO audits in a single workflow. Most competitors do only one piece." },
    { question: "Does SEOintent rank content on AI engines like ChatGPT and Claude?", answer: "Yes. SEOintent uses AEO (Answer Engine Optimization) principles — structured data, direct answer formatting, entity-first writing — so your content is cited by ChatGPT, Claude, Gemini, Perplexity, and Google AI Overviews, not just traditional Google results." },
    { question: "What does SEOintent cost?", answer: "SEOintent offers a free 14-day trial, then Growth at $99/month and Scale at $199/month. Annual plans get 25% off. No credit card required for trial." },
    { question: "Which CMS platforms does SEOintent publish to?", answer: "SEOintent publishes directly to WordPress (via REST API) and Shopify (via Admin API). More integrations like Webflow and Ghost are on the roadmap." },
  ];

  return (
    <div className="min-h-screen bg-white text-zinc-900 selection:bg-[#FF2F2F]/20 selection:text-[#FF2F2F] overflow-x-clip" data-testid="homepage">
      <SEO
        title="AI SEO Services & Platform for Agencies"
        description="SEOintent is the AI SEO platform for agencies and small businesses. Auto-cluster keywords, generate SEO articles with Claude, rank on Google + ChatGPT + Perplexity. Free 14-day trial."
        canonical="https://seointent.com"
        image="https://seointent.com/og/home.png"
        keywords="ai seo services, ai seo platform, ai seo tools, claude seo tool, llm seo tracking, saas seo tools, best paid seo tools, ai seo for agencies, programmatic seo, aeo, answer engine optimization"
        schemas={[
          organizationSchema,
          websiteSchema,
          softwareApplicationSchema,
          faqSchema(homepageFaqs),
        ]}
      />
      <TopBanner />

      {/* Animated Background — absolute (not fixed) so it doesn't create a stacking layer on scroll */}
      <div className="absolute inset-0 -z-20 pointer-events-none overflow-hidden">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-[#FF2F2F]/5 rounded-full blur-[80px] animate-blob-1" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[80px] animate-blob-2" />
      </div>

      <MegaNav stickyTopClass="top-9" />

      <main>
        {/* Hero Section */}
        <section className="relative pt-12 md:pt-16 pb-8 md:pb-20 overflow-hidden">
          {/* Atmosphere — pure-CSS aurora mesh + fading grid. On-brand, vector-crisp, no raster. */}
          <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(255,47,47,0.10),transparent_60%)]" />
            <div className="animate-aurora absolute -top-28 left-1/2 h-[540px] w-[860px] -translate-x-1/2 rounded-full bg-[#FF2F2F]/10 blur-[120px]" />
            <div className="animate-aurora absolute -top-10 right-[4%] h-[380px] w-[380px] rounded-full bg-[#002FA7]/10 blur-[110px]" style={{ animationDelay: "-8s" }} />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(9,9,11,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(9,9,11,0.04)_1px,transparent_1px)] bg-[size:44px_44px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000,transparent_75%)]" />
          </div>
          <div className="container mx-auto px-4 text-center">
            {/* Hero content — CSS fade-in, no Framer Motion (keeps motion out of synchronous bundle) */}
            <div className="animate-hero-in">
              <span className="inline-block mb-3 py-1 px-4 border border-[#FF2F2F]/20 bg-[#FF2F2F]/5 text-[#FF2F2F] text-xs sm:text-sm font-medium rounded-full">
                Built for the AI search era · AEO + GEO + LLMO
              </span>
              <h1 className="mx-auto max-w-4xl text-[30px] sm:text-5xl md:text-6xl font-bold tracking-tighter mb-5">
                Rank on Top of AI & Google
                <br />
                <span className="text-[#FF2F2F]">Get Suggested by ChatGPT, Gemini & Claude</span>
              </h1>
              <p className="text-[15px] sm:text-lg md:text-xl text-zinc-500 max-w-2xl mx-auto mb-6 md:mb-10 leading-relaxed">
                Rank in <span className="text-zinc-900 font-semibold">AI Overviews, ChatGPT, Claude, Gemini & Perplexity</span> — and Google's top 10. One platform for keyword research, content, backlinks, publishing, and AI-answer tracking.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <div className="flex w-full max-w-md items-center space-x-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 h-12 px-4 border border-zinc-300 rounded-lg bg-white text-sm focus:outline-none focus:border-[#FF2F2F] transition-colors"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="hero-email-input"
                  />
                  <button onClick={handleEmailCapture} className="h-12 px-6 sm:px-8 bg-[#FF2F2F] hover:bg-[#e02828] text-white font-semibold rounded-lg shadow-lg shadow-[#FF2F2F]/20 flex items-center gap-2 transition-colors whitespace-nowrap" data-testid="hero-cta-btn">
                    Start Now <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Trust Signals */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-zinc-500">
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-zinc-200 overflow-hidden">
                        <img src={`https://picsum.photos/seed/user${i}/32/32`} alt="" referrerPolicy="no-referrer" />
                      </div>
                    ))}
                  </div>
                  <span className="ml-2">Trusted by hundreds of SEOs</span>
                </div>
                <div className="flex items-center gap-1 text-yellow-500">
                  <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
                  <span className="text-zinc-900 font-bold ml-1">4.9/5 on G2</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#FF2F2F]" />
                  <span>No Credit Card Required</span>
                </div>
              </div>
              <p className="mt-4 text-sm text-zinc-400">No credit card required. Start your 14-day free trial.</p>
            </div>

            {/* Signature product visual — exclusively designed, fully coded (SVG + CSS),
                vector-crisp on retina and in the exact brand palette. Replaces the old
                AI-generated raster screenshot. */}
            <div className="mt-10 md:mt-16">
              <HeroConsole />
            </div>
          </div>
        </section>

        {/* ── Signature workflow graphic: the whole pipeline as one system ── */}
        <section className="relative py-14 md:py-20 border-t border-zinc-100">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
              <span className="mb-3 inline-block rounded-full border border-[#FF2F2F]/20 bg-[#FF2F2F]/5 px-4 py-1 text-xs font-semibold text-[#FF2F2F]">
                One platform · not five tabs
              </span>
              <h2 className="font-cabinet text-3xl font-bold tracking-tight md:text-[42px] md:whitespace-nowrap">
                Keyword to <span className="text-[#FF2F2F]">cited by AI</span> — in one flow
              </h2>
              <p className="mt-3 text-zinc-500 md:text-lg">
                Every step feeds the next. No exporting CSVs between five tools — SEOintent runs the whole SEO system for you.
              </p>
            </div>
            <WorkflowStrip />
          </div>
        </section>

        <AIEnginesMarquee />
        <Suspense fallback={<SectionFallback />}><LogoMarquee /></Suspense>

        {/* Positioning Section */}
        <motion.section id="features" className="py-24 border-t border-zinc-200" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.4, ease: "easeOut" }}>
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
                  More Than an AI Writing Tool — <br />
                  <span className="text-[#FF2F2F] italic">This Is a Complete AI SEO System</span>
                </h2>
                <p className="text-lg text-zinc-500 mb-8">
                  Most tools help you write content. SEOintent helps you <span className="text-[#FF2F2F] font-semibold">rank content</span>.
                  Everything works together — like a real SEO strategy should.
                </p>
                <ul className="space-y-4">
                  {["Discover high-impact keywords", "Structure content for search intent", "Generate optimized articles", "Automate internal linking", "Publish instantly", "Track rankings and performance"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#FF2F2F]" /><span>{item}</span></li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Search className="w-6 h-6 text-[#FF2F2F]" />, t: "Keyword Intel", d: "Find real opportunities using actual search data." },
                  { icon: <Layers className="w-6 h-6 text-[#FF2F2F]" />, t: "Smart Clustering", d: "Build topical authority with structured clusters." },
                  { icon: <FileText className="w-6 h-6 text-[#FF2F2F]" />, t: "SEO Content", d: "Optimized headings, structure, and schema markup." },
                  { icon: <BarChart3 className="w-6 h-6 text-[#FF2F2F]" />, t: "Rank Tracking", d: "Track rankings, traffic, and growth in real-time." },
                ].map((card, i) => (
                  <div key={i} className={`group rounded-2xl border border-zinc-200 bg-white p-6 transition-all duration-200 hover:border-[#FF2F2F]/30 hover:shadow-lg ${i % 2 === 1 ? "mt-8" : ""}`}>
                    <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF2F2F]/10 transition-transform duration-200 group-hover:scale-105">
                      {card.icon}
                    </span>
                    <h3 className="font-cabinet font-bold tracking-tight mb-2">{card.t}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{card.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── GEO Audit Checklist Email Capture ── */}
        <motion.section
          className="py-20 relative overflow-hidden"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#002FA7]/8 via-white to-[#FF2F2F]/5" />
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <span className="inline-block mb-4 py-1 px-4 border border-[#002FA7]/20 bg-[#002FA7]/5 text-[#002FA7] text-xs font-bold rounded-full tracking-wide uppercase">
                Free PDF · Instant Delivery
              </span>
              <h2 className="font-cabinet text-3xl md:text-4xl font-black tracking-tight mb-4">
                Get Your Free <span className="text-[#FF2F2F]">GEO Audit Checklist</span>
              </h2>
              <p className="text-zinc-500 text-lg mb-8">
                10 things AI search engines check before citing your site — free PDF, instant delivery
              </p>

              {checklistDone ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center justify-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-5 text-emerald-700 font-medium"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span>✓ Check your inbox at <strong>{checklistEmail}</strong>. If it's not there within 2 mins, check spam and whitelist <strong>hello@noreply.seointent.com</strong>.</span>
                </motion.div>
              ) : (
                <form onSubmit={handleChecklistSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    required
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-3.5 border border-zinc-300 rounded-xl bg-white text-sm focus:outline-none focus:border-[#FF2F2F] transition-colors"
                    value={checklistEmail}
                    onChange={(e) => setChecklistEmail(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={checklistLoading}
                    className="px-6 py-3.5 bg-[#FF2F2F] hover:bg-[#e02828] text-white font-semibold rounded-xl shadow-lg shadow-[#FF2F2F]/20 transition-colors disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {checklistLoading ? "Sending..." : <><span>Send Me The Checklist</span> <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )}

              <p className="mt-4 text-xs text-zinc-400">No spam. Unsubscribe any time.</p>
            </div>
          </div>
        </motion.section>

        {/* Latest Blog Posts */}
        <motion.section className="py-24 bg-zinc-50/50 border-y border-zinc-200 overflow-hidden" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.4, ease: "easeOut" }}>
          <div className="container mx-auto px-4">
            {/* Header */}
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="inline-block bg-[#FF2F2F]/10 text-[#FF2F2F] text-xs font-bold px-3 py-1 rounded-full mb-3 tracking-wide uppercase">Latest Articles</span>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight">From the <span className="text-[#FF2F2F]">Blog</span></h2>
                <p className="text-zinc-500 mt-2 text-lg">SEO & AI search strategies, updated as the industry evolves.</p>
              </div>
              <Link to="/blog" className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-[#FF2F2F] hover:underline flex-shrink-0 ml-6">
                All articles <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Cards — horizontal scroll on mobile, 3-col grid on md+ */}
            <div className="
              flex gap-5 overflow-x-auto scrollbar-hide pb-3 -mx-4 px-4 snap-x snap-mandatory
              md:grid md:grid-cols-3 md:overflow-x-visible md:gap-6 md:pb-0 md:mx-0 md:px-0 md:snap-none
            " style={{ WebkitOverflowScrolling: "touch" }}>
              {homeBlogPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="
                    group flex flex-col bg-white border border-zinc-200 rounded-2xl overflow-hidden
                    hover:border-[#FF2F2F]/40 hover:shadow-xl transition-[border-color,box-shadow] duration-200
                    shrink-0 w-[82vw] snap-start
                    md:shrink md:w-auto md:snap-none
                  "
                >
                  {/* Thumbnail */}
                  {post.image && (
                    <div className="aspect-video overflow-hidden bg-zinc-100 flex-shrink-0">
                      <img
                        src={post.image}
                        alt={post.altText || post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  )}

                  {/* Body */}
                  <div className="p-5 flex flex-col flex-1">
                    {post.category && (
                      <span className={`self-start text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full mb-3 ${homeCatCls(post.category)}`}>
                        {post.category}
                      </span>
                    )}
                    <h3 className="font-cabinet font-bold text-base leading-snug mb-2 group-hover:text-[#FF2F2F] transition-colors duration-150 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-100">
                      <span className="text-xs text-zinc-400">{post.readTime}</span>
                      <span className="text-xs font-semibold text-[#FF2F2F] flex items-center gap-0.5">
                        Read more <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Mobile "All articles" */}
            <div className="mt-6 text-center md:hidden">
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF2F2F] hover:underline">
                View all articles <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </motion.section>

        <Suspense fallback={<SectionFallback />}><NewFeaturesSection /></Suspense>
        <Suspense fallback={<SectionFallback />}><ComparisonTable /></Suspense>

        {/* Video Testimonials — wrapped in Suspense because VideoCard is lazy-loaded */}
        <Suspense fallback={<SectionFallback />}>
        <motion.section id="how-it-works" className="py-24 overflow-hidden bg-zinc-50/50" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.4, ease: "easeOut" }}>
          <div className="container mx-auto px-4 mb-16">
            <div className="grid lg:grid-cols-5 gap-10 items-center max-w-6xl mx-auto">
              <div className="lg:col-span-3 text-center lg:text-left">
                <span className="inline-block mb-4 py-1 px-4 border border-[#FF2F2F]/20 bg-[#FF2F2F]/5 text-[#FF2F2F] text-sm font-medium rounded-full">Trusted by hundreds of SEO Professionals</span>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Real Results from <span className="text-[#FF2F2F]">Real Users</span></h2>
              </div>
              <div className="lg:col-span-2 hidden lg:block">
                <ResultsPanel />
              </div>
            </div>
          </div>
          <div className="space-y-12">
            <div className="flex overflow-hidden" onMouseEnter={() => setIsHoveredRow1(true)} onMouseLeave={() => setIsHoveredRow1(false)}>
              <motion.div className="flex gap-8 whitespace-nowrap" animate={{ x: isHoveredRow1 ? undefined : [0, -2592] }} transition={{ duration: 50, repeat: Infinity, ease: "linear" }}>
                {[...row1Videos, ...row1Videos, ...row1Videos].map((id, i) => <VideoCard key={`r1-${i}`} videoId={id} />)}
              </motion.div>
            </div>
            <div className="flex overflow-hidden" onMouseEnter={() => setIsHoveredRow2(true)} onMouseLeave={() => setIsHoveredRow2(false)}>
              <motion.div className="flex gap-8 whitespace-nowrap" animate={{ x: isHoveredRow2 ? undefined : [-3024, 0] }} transition={{ duration: 60, repeat: Infinity, ease: "linear" }}>
                {[...row2Videos, ...row2Videos, ...row2Videos].map((id, i) => <VideoCard key={`r2-${i}`} videoId={id} />)}
              </motion.div>
            </div>
          </div>
        </motion.section>
        </Suspense>

        <Suspense fallback={<SectionFallback />}><ConversionSections /></Suspense>

        {/* Keyword Checker */}
        <motion.section className="py-24" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.4, ease: "easeOut" }}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-3xl border border-[#FF2F2F]/20 bg-[#FF2F2F]/5 p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="flex-1">
                  <span className="inline-block mb-4 bg-[#FF2F2F]/10 text-[#FF2F2F] text-xs font-bold px-3 py-1 rounded-full border border-[#FF2F2F]/20">PREMIUM FEATURE</span>
                  <h2 className="text-3xl font-bold mb-4">Try Our <span className="text-[#FF2F2F]">AI Keyword</span> Intelligence</h2>
                  <p className="text-zinc-500 mb-6">Enter a seed keyword to see how our AI clusters opportunities and maps search intent.</p>
                  <div className="flex gap-2">
                    <input placeholder="e.g. 'best coffee machines'" className="flex-1 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#FF2F2F]" value={keyword} onChange={(e) => setKeyword(e.target.value)} data-testid="keyword-input" />
                    <button onClick={handleAnalyze} disabled={isAnalyzing} className="bg-[#FF2F2F] hover:bg-[#e02828] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50" data-testid="keyword-analyze-btn">
                      {isAnalyzing ? "Analyzing..." : "Analyze"}
                    </button>
                  </div>
                </div>
                <div className="w-full md:w-72 space-y-3">
                  {showResults ? (
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-3">
                      <div className="p-4 rounded-xl bg-white border border-[#FF2F2F]/30 shadow-sm">
                        <div className="flex justify-between items-center mb-2"><span className="text-xs font-medium text-zinc-500">Difficulty</span><span className="text-xs font-bold text-[#FF2F2F]">Medium (45)</span></div>
                        <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-[#FF2F2F] w-[45%]" /></div>
                      </div>
                      <div className="p-4 rounded-xl bg-white border border-[#FF2F2F]/30 shadow-sm">
                        <div className="flex justify-between items-center mb-2"><span className="text-xs font-medium text-zinc-500">Volume</span><span className="text-xs font-bold">12.5K / mo</span></div>
                        <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-[#FF2F2F] w-[80%]" /></div>
                      </div>
                      <div className="p-4 rounded-xl bg-white border border-[#FF2F2F]/30 shadow-sm">
                        <p className="text-xs font-medium text-zinc-500 mb-2">AI Intent Mapping</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-[10px] bg-[#FF2F2F]/10 text-[#FF2F2F] px-2 py-0.5 rounded-full border border-[#FF2F2F]/20 font-bold">Commercial</span>
                          <span className="text-[10px] bg-blue-500/10 text-blue-500 px-2 py-0.5 rounded-full border border-blue-500/20 font-bold">Informational</span>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="space-y-3 opacity-50 grayscale">
                      <div className="p-4 rounded-xl bg-white border border-zinc-200 shadow-sm">
                        <div className="flex justify-between items-center mb-2"><span className="text-xs font-medium text-zinc-500">Difficulty</span><span className="text-xs font-bold text-zinc-400">--</span></div>
                        <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden" />
                      </div>
                      <div className="p-4 rounded-xl bg-white border border-zinc-200 shadow-sm">
                        <div className="flex justify-between items-center mb-2"><span className="text-xs font-medium text-zinc-500">Volume</span><span className="text-xs font-bold text-zinc-400">--</span></div>
                        <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden" />
                      </div>
                      <div className="p-4 rounded-xl bg-white border border-zinc-200 shadow-sm">
                        <p className="text-xs font-medium text-zinc-500 mb-2">AI Intent Mapping</p>
                        <div className="flex flex-wrap gap-2"><div className="h-4 w-16 bg-zinc-100 rounded" /><div className="h-4 w-20 bg-zinc-100 rounded" /></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.4, ease: "easeOut" }}>
          <Suspense fallback={<SectionFallback />}><PricingSection onSelectPlan={handleSelectPlan} /></Suspense>
        </motion.div>

        {/* Final CTA */}
        <motion.section id="demo" className="py-24 relative overflow-hidden" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.4, ease: "easeOut" }}>
          <div className="absolute inset-0 -z-10 bg-[#FF2F2F]/5" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto mb-14 hidden md:block">
              <Suspense fallback={<SectionFallback />}><AICitationShowcase /></Suspense>
            </div>
            <div className="text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight">Try It Free. See the Difference in Your <span className="text-[#FF2F2F]">First Workflow.</span></h2>
              <p className="text-xl text-zinc-500 mb-12">No long setup. No steep learning curve. <br />Just log in... and watch your SEO system start working.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <button onClick={() => setAuthOpen(true)} className="h-14 px-10 text-lg bg-[#FF2F2F] hover:bg-[#e02828] text-white font-bold rounded-xl shadow-xl shadow-[#FF2F2F]/20 transition-colors">Start Free Trial</button>
                <button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="h-14 px-10 text-lg border border-[#FF2F2F]/20 hover:bg-[#FF2F2F]/5 text-zinc-900 font-bold rounded-xl transition-colors">Upgrade When You're Ready</button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-medium text-zinc-500 mb-8">
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#FF2F2F]" /><span>Enterprise-Grade Security</span></div>
                <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#FF2F2F]" /><span>99.9% Uptime Guarantee</span></div>
                <div className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#FF2F2F]" /><span>Instant Setup</span></div>
              </div>
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-zinc-500 font-medium">
                <span>No credit card required</span>
                <span>Setup takes less than 5 minutes</span>
                <span>Works with your existing website</span>
              </div>
            </div>
            </div>
          </div>
        </motion.section>

        {/* Free Tools Section — lead funnel (Stream 6) */}
        <section className="py-20 border-t border-zinc-200">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-5 gap-10 items-center mb-12">
              <div className="lg:col-span-3 text-center lg:text-left">
                <span className="inline-block bg-[#FF2F2F]/10 text-[#FF2F2F] text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wide uppercase">Free tools · No signup</span>
                <h2 className="font-cabinet font-black text-4xl sm:text-5xl tracking-tighter mb-4">Try SEOintent in 30 seconds</h2>
                <p className="text-zinc-500 text-lg max-w-xl">Run any of these audits on your site right now. No signup, no card, no email required.</p>
              </div>
              <div className="lg:col-span-2 hidden lg:block">
                <Suspense fallback={<SectionFallback />}><ToolResultCard /></Suspense>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-6xl mx-auto">
              <Link to="/tools/ai-content-detector" className="group bg-white border-2 border-[#FF2F2F]/30 rounded-2xl p-5 hover:border-[#FF2F2F] hover:shadow-lg transition-all relative">
                <span className="absolute top-3 right-3 bg-[#FF2F2F] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">New</span>
                <div className="w-10 h-10 bg-[#FF2F2F]/10 rounded-xl flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-[#FF2F2F]" />
                </div>
                <h3 className="font-cabinet font-bold text-lg tracking-tight mb-1 group-hover:text-[#FF2F2F]">AI Content Detector</h3>
                <p className="text-sm text-zinc-500 mb-3">Score any text 0-100 — Human vs AI breakdown with rewrite suggestions for flagged passages.</p>
                <span className="text-xs text-[#FF2F2F] font-bold">Check text →</span>
              </Link>

              <Link to="/tools/ai-visibility-checker" className="group bg-white border border-zinc-200 rounded-2xl p-5 hover:border-[#FF2F2F]/40 hover:shadow-lg transition-all">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="font-cabinet font-bold text-lg tracking-tight mb-1 group-hover:text-[#FF2F2F]">AI Visibility Checker</h3>
                <p className="text-sm text-zinc-500 mb-3">Score how likely Claude, ChatGPT, Perplexity, and Gemini are to cite your page. 0-100 with top 3 fixes.</p>
                <span className="text-xs text-[#FF2F2F] font-bold">Run scan →</span>
              </Link>

              <Link to="/tools/meta-tag-analyzer" className="group bg-white border border-zinc-200 rounded-2xl p-5 hover:border-[#FF2F2F]/40 hover:shadow-lg transition-all">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                  <Search className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-cabinet font-bold text-lg tracking-tight mb-1 group-hover:text-[#FF2F2F]">Meta Tag Analyzer</h3>
                <p className="text-sm text-zinc-500 mb-3">Audit any URL's title, description, OG tags, Twitter Cards, canonical, robots, H1. Pass/warn/fail.</p>
                <span className="text-xs text-[#FF2F2F] font-bold">Audit page →</span>
              </Link>

              <Link to="/tools/sitemap-analyzer" className="group bg-white border border-zinc-200 rounded-2xl p-5 hover:border-[#FF2F2F]/40 hover:shadow-lg transition-all">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mb-3">
                  <Globe className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-cabinet font-bold text-lg tracking-tight mb-1 group-hover:text-[#FF2F2F]">Sitemap & Robots Analyzer</h3>
                <p className="text-sm text-zinc-500 mb-3">Find crawl blocks, missing sitemap declarations, stale URLs, and robots.txt conflicts.</p>
                <span className="text-xs text-[#FF2F2F] font-bold">Run audit →</span>
              </Link>

              <Link to="/tools/schema-generator" className="group bg-white border border-zinc-200 rounded-2xl p-5 hover:border-[#FF2F2F]/40 hover:shadow-lg transition-all">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-cabinet font-bold text-lg tracking-tight mb-1 group-hover:text-[#FF2F2F]">Schema Generator</h3>
                <p className="text-sm text-zinc-500 mb-3">Generate valid JSON-LD for Article, FAQPage, HowTo, Product, LocalBusiness — pick a type, fill fields, copy.</p>
                <span className="text-xs text-[#FF2F2F] font-bold">Generate schema →</span>
              </Link>
            </div>

            <p className="text-center text-xs text-zinc-500 mt-8">All tools cap at 15 free runs per day per IP · No login required</p>
          </div>
        </section>

        {/* Learn / Resources Section */}
        <section className="py-20 bg-zinc-50 border-t border-zinc-200">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#FF2F2F]/10 text-[#FF2F2F] text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wide uppercase">Free resources</span>
              <h2 className="font-cabinet font-black text-4xl sm:text-5xl tracking-tighter mb-4">Learn SEO & GEO</h2>
              <p className="text-zinc-500 text-lg max-w-xl mx-auto">Everything you need to rank in Google and get cited by AI engines — in plain English.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
              {/* SEO Guide Pillar */}
              <Link to="/seo-guide" className="group bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#FF2F2F]/40 hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Search className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">SEO Guide</span>
                </div>
                <h3 className="font-cabinet font-bold text-xl tracking-tight mb-2 group-hover:text-[#FF2F2F] transition-colors">The Complete Search Intent SEO Guide</h3>
                <p className="text-sm text-zinc-500 leading-relaxed mb-4">How to identify search intent, match content format to SERP expectations, and rank for every keyword you target.</p>
                <div className="flex flex-wrap gap-2">
                  {["What is Search Intent", "Keyword Research", "Content Strategy"].map(tag => (
                    <span key={tag} className="text-xs bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </Link>

              {/* GEO Guide Pillar */}
              <Link to="/geo-guide" className="group bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#FF2F2F]/40 hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#002FA7]/10 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#002FA7]" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#002FA7] bg-[#002FA7]/10 px-2.5 py-1 rounded-full">GEO Guide</span>
                </div>
                <h3 className="font-cabinet font-bold text-xl tracking-tight mb-2 group-hover:text-[#FF2F2F] transition-colors">The Complete GEO Guide</h3>
                <p className="text-sm text-zinc-500 leading-relaxed mb-4">How to get cited in ChatGPT, Perplexity, and Google AI Overviews using the 9 Princeton GEO methods.</p>
                <div className="flex flex-wrap gap-2">
                  {["Rank in ChatGPT", "AI Citations", "GEO vs SEO"].map(tag => (
                    <span key={tag} className="text-xs bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </Link>
            </div>

            {/* Child page quick links */}
            <div className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {[
                { to: "/what-is-search-intent", label: "What is Search Intent?", tag: "SEO · 8 min" },
                { to: "/search-intent-keyword-research", label: "Intent Keyword Research", tag: "SEO · 10 min" },
                { to: "/geo-vs-seo", label: "GEO vs SEO", tag: "GEO · 9 min" },
                { to: "/how-to-rank-in-chatgpt", label: "How to Rank in ChatGPT", tag: "GEO · 11 min" },
                { to: "/ai-citation-tracking-guide", label: "AI Citation Tracking", tag: "GEO · 9 min" },
                { to: "/blog", label: "Blog →", tag: "All articles" },
              ].map(l => (
                <Link key={l.to} to={l.to} className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-xl hover:border-[#FF2F2F]/40 hover:text-[#FF2F2F] transition-all group">
                  <span className="text-sm font-medium text-zinc-700 group-hover:text-[#FF2F2F]">{l.label}</span>
                  <span className="text-xs text-zinc-400">{l.tag}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
